import { toast } from 'react-toastify';

import { getSerializedErrorMessage } from '../../../../../utils/errors';
import {
  ensureIntakeFamily,
  getIntakeFamilyAdditionalInfoArray,
  setIntakeFamilyAdditionalInfoArray,
} from './utils';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type {
  IClientSibling,
  IFamily,
  IFamilyMember,
  IFamilyValueItem,
} from '../../../../../pages/common';
import type { IntakeQuestionnaireState } from '../../types';
import type {
  CreateIntakeFamilyAdditionalInfoPayload,
  DeleteIntakeClientSiblingPayload,
  DeleteIntakeFamilyAdditionalInfoPayload,
  DeleteIntakeFamilyMemberPayload,
  IntakeFamilyThunks,
} from './types';

export function addIntakeFamilyCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeFamilyThunks,
) {
  addUpdateIntakeFamilyCases(builder, thunks);
  addCreateIntakeFamilyMemberCases(builder, thunks);
  addUpdateIntakeFamilyMemberCases(builder, thunks);
  addDeleteIntakeFamilyMemberCases(builder, thunks);
  addCreateIntakeClientSiblingCases(builder, thunks);
  addUpdateIntakeClientSiblingCases(builder, thunks);
  addDeleteIntakeClientSiblingCases(builder, thunks);
  addCreateIntakeFamilyAdditionalInfoCases(builder, thunks);
  addDeleteIntakeFamilyAdditionalInfoCases(builder, thunks);
}

function addUpdateIntakeFamilyCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeFamilyThunks,
) {
  const { updateIntakeFamily } = thunks;

  builder
    .addCase(updateIntakeFamily.pending, (state) => {
      state.updateFamilyLoading = true;
      state.error = null;
    })
    .addCase(updateIntakeFamily.fulfilled, (state, action: PayloadAction<IFamily>) => {
      state.updateFamilyLoading = false;
      if (!state.intakeQuestionnaireDetails) return;
      // Same snapshot race as updateIntakeMedicalInfo: the scalar PATCH response embeds
      // the member/sibling/value-item lists as of when the backend handled it, so adopting
      // them wholesale rolls back list changes saved while it was in flight. The lists are
      // maintained by their own reducers — keep the store's copies.
      const currentFamily = state.intakeQuestionnaireDetails.family;
      state.intakeQuestionnaireDetails.family = currentFamily
        ? {
            ...action.payload,
            family_members: currentFamily.family_members,
            client_siblings: currentFamily.client_siblings,
            maternal_medical_history: currentFamily.maternal_medical_history,
            paternal_medical_history: currentFamily.paternal_medical_history,
            community_support: currentFamily.community_support,
          }
        : action.payload;
    })
    .addCase(updateIntakeFamily.rejected, (state, action) => {
      state.updateFamilyLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addCreateIntakeFamilyMemberCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeFamilyThunks,
) {
  const { createIntakeFamilyMember } = thunks;

  builder
    .addCase(createIntakeFamilyMember.pending, (state) => {
      state.createFamilyMemberLoading = true;
      state.error = null;
    })
    .addCase(
      createIntakeFamilyMember.fulfilled,
      (state, action: PayloadAction<IFamilyMember>) => {
        state.createFamilyMemberLoading = false;
        if (!state.intakeQuestionnaireDetails) return;
        ensureIntakeFamily(state.intakeQuestionnaireDetails).family_members.unshift(action.payload);
      },
    )
    .addCase(createIntakeFamilyMember.rejected, (state, action) => {
      state.createFamilyMemberLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addUpdateIntakeFamilyMemberCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeFamilyThunks,
) {
  const { updateIntakeFamilyMember } = thunks;

  builder
    .addCase(updateIntakeFamilyMember.pending, (state) => {
      state.updateFamilyMemberLoading = true;
      state.error = null;
    })
    .addCase(
      updateIntakeFamilyMember.fulfilled,
      (state, action: PayloadAction<IFamilyMember>) => {
        state.updateFamilyMemberLoading = false;
        if (!state.intakeQuestionnaireDetails?.family) return;
        const list = state.intakeQuestionnaireDetails.family.family_members;
        const index = list.findIndex((row) => row.id === action.payload.id);
        if (index !== -1) list[index] = action.payload;
      },
    )
    .addCase(updateIntakeFamilyMember.rejected, (state, action) => {
      state.updateFamilyMemberLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addDeleteIntakeFamilyMemberCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeFamilyThunks,
) {
  const { deleteIntakeFamilyMember } = thunks;

  builder
    .addCase(deleteIntakeFamilyMember.pending, (state) => {
      state.deleteFamilyMemberLoading = true;
      state.error = null;
    })
    .addCase(
      deleteIntakeFamilyMember.fulfilled,
      (state, action: PayloadAction<void, string, { arg: DeleteIntakeFamilyMemberPayload }>) => {
        state.deleteFamilyMemberLoading = false;
        if (!state.intakeQuestionnaireDetails?.family) return;
        state.intakeQuestionnaireDetails.family.family_members =
          state.intakeQuestionnaireDetails.family.family_members.filter(
            (row) => row.id !== action.meta.arg.memberId,
          );
      },
    )
    .addCase(deleteIntakeFamilyMember.rejected, (state, action) => {
      state.deleteFamilyMemberLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addCreateIntakeClientSiblingCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeFamilyThunks,
) {
  const { createIntakeClientSibling } = thunks;

  builder
    .addCase(createIntakeClientSibling.pending, (state) => {
      state.createClientSiblingLoading = true;
      state.error = null;
    })
    .addCase(
      createIntakeClientSibling.fulfilled,
      (state, action: PayloadAction<IClientSibling>) => {
        state.createClientSiblingLoading = false;
        if (!state.intakeQuestionnaireDetails) return;
        ensureIntakeFamily(state.intakeQuestionnaireDetails).client_siblings.push(action.payload);
      },
    )
    .addCase(createIntakeClientSibling.rejected, (state, action) => {
      state.createClientSiblingLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addUpdateIntakeClientSiblingCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeFamilyThunks,
) {
  const { updateIntakeClientSibling } = thunks;

  builder
    .addCase(updateIntakeClientSibling.pending, (state) => {
      state.updateClientSiblingLoading = true;
      state.error = null;
    })
    .addCase(
      updateIntakeClientSibling.fulfilled,
      (state, action: PayloadAction<IClientSibling>) => {
        state.updateClientSiblingLoading = false;
        if (!state.intakeQuestionnaireDetails?.family) return;
        const list = state.intakeQuestionnaireDetails.family.client_siblings;
        const index = list.findIndex((row) => row.id === action.payload.id);
        if (index !== -1) list[index] = action.payload;
      },
    )
    .addCase(updateIntakeClientSibling.rejected, (state, action) => {
      state.updateClientSiblingLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addDeleteIntakeClientSiblingCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeFamilyThunks,
) {
  const { deleteIntakeClientSibling } = thunks;

  builder
    .addCase(deleteIntakeClientSibling.pending, (state) => {
      state.deleteClientSiblingLoading = true;
      state.error = null;
    })
    .addCase(
      deleteIntakeClientSibling.fulfilled,
      (state, action: PayloadAction<void, string, { arg: DeleteIntakeClientSiblingPayload }>) => {
        state.deleteClientSiblingLoading = false;
        if (!state.intakeQuestionnaireDetails?.family) return;
        state.intakeQuestionnaireDetails.family.client_siblings =
          state.intakeQuestionnaireDetails.family.client_siblings.filter(
            (row) => row.id !== action.meta.arg.siblingId,
          );
      },
    )
    .addCase(deleteIntakeClientSibling.rejected, (state, action) => {
      state.deleteClientSiblingLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addCreateIntakeFamilyAdditionalInfoCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeFamilyThunks,
) {
  const { createIntakeFamilyAdditionalInfo } = thunks;

  builder
    .addCase(createIntakeFamilyAdditionalInfo.pending, (state) => {
      state.createFamilyAdditionalInfoLoading = true;
      state.error = null;
    })
    .addCase(
      createIntakeFamilyAdditionalInfo.fulfilled,
      (
        state,
        action: PayloadAction<
          IFamilyValueItem,
          string,
          { arg: CreateIntakeFamilyAdditionalInfoPayload }
        >,
      ) => {
        state.createFamilyAdditionalInfoLoading = false;
        if (!state.intakeQuestionnaireDetails) return;
        getIntakeFamilyAdditionalInfoArray(
          ensureIntakeFamily(state.intakeQuestionnaireDetails),
          action.meta.arg.data.info_type,
        ).push(action.payload);
      },
    )
    .addCase(createIntakeFamilyAdditionalInfo.rejected, (state, action) => {
      state.createFamilyAdditionalInfoLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addDeleteIntakeFamilyAdditionalInfoCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeFamilyThunks,
) {
  const { deleteIntakeFamilyAdditionalInfo } = thunks;

  builder
    .addCase(deleteIntakeFamilyAdditionalInfo.pending, (state) => {
      state.deleteFamilyAdditionalInfoLoading = true;
      state.error = null;
    })
    .addCase(
      deleteIntakeFamilyAdditionalInfo.fulfilled,
      (
        state,
        action: PayloadAction<void, string, { arg: DeleteIntakeFamilyAdditionalInfoPayload }>,
      ) => {
        state.deleteFamilyAdditionalInfoLoading = false;
        const { family } = state.intakeQuestionnaireDetails ?? {};
        if (!family) return;
        const { infoType, itemId } = action.meta.arg;
        setIntakeFamilyAdditionalInfoArray(
          family,
          infoType,
          getIntakeFamilyAdditionalInfoArray(family, infoType).filter((row) => row.id !== itemId),
        );
      },
    )
    .addCase(deleteIntakeFamilyAdditionalInfo.rejected, (state, action) => {
      state.deleteFamilyAdditionalInfoLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}
